import getSchema from '@which/reviews-filter-definitions'
import type { FilterData } from '@which/shared'
import { getOptionValue } from '@which/shared'

import type { NeedsBasedFilterMap, NeedsBasedQueryValues } from '../../../../shared/types'
import { merge } from '../../../../shared/utils/merge'
import { set } from '../../../../shared/utils/set'
import { matchedNeedsBasedOptionsAndLabels } from './matched-needs-based-options-and-labels'

export const mapFilterQueryParams = (
  getFilterQueryParams: () => {
    basicFilterParams: RegExpMatchArray | null
    needsBasedFilterParams: RegExpMatchArray | null
    filterCategory: RegExpMatchArray | null
  },
  getQueryParamValue: (param: string) => RegExpMatchArray | null
) => {
  const { basicFilterParams, needsBasedFilterParams, filterCategory } = getFilterQueryParams()
  const [, category] = filterCategory || []
  const filterSchemas = getSchema(category) || []
  const isSingleBrand =
    basicFilterParams?.length === 1 &&
    /filters.manufacturers.manufacturer=[\w-]*(?!%2).$/.test(basicFilterParams?.[0])

  const basicFilterDataFromQueryParams =
    basicFilterParams?.reduce((basicFilterData: FilterData, param) => {
      const queryParamValue = getQueryParamValue(param)?.[1]

      /* v8 ignore else */
      if (queryParamValue) {
        const queryValue = isSingleBrand ? queryParamValue.replace(/(-)/g, '_') : queryParamValue

        set(basicFilterData, getPath(param), getValue(queryValue))
      }

      return basicFilterData
    }, {}) || {}

  const needsBasedQueryValuesFromQueryParams =
    needsBasedFilterParams?.reduce((needsBasedQueryValues: NeedsBasedQueryValues, param) => {
      const queryParamValue = getQueryParamValue(param)?.[1]
      /* v8 ignore else */
      if (queryParamValue) {
        set(needsBasedQueryValues, getPath(param).replace(/nbf./gi, ''), getValue(queryParamValue))
      }

      return needsBasedQueryValues
    }, {}) || {}

  const { needsBasedFilterDataFromQueryParams = {}, needsBasedSelectedMapFromQueryParams = {} } =
    needsBasedFilterParams?.reduce(
      (needsBasedFilterMap: NeedsBasedFilterMap, param) => {
        const [, matchedParam] = param.match(/^(?:[^\.]*\.){3}(.*)/) || []
        const [, queryParamValue] = getQueryParamValue(matchedParam) || []

        const bucketLabel = getPath(param)
          .replace(/nbf./gi, '')
          ?.match(/^([^.]+)/)?.[0]

        const optionCode = getPath(param)?.match(/([^.]+)$/)?.[0]
        /* v8 ignore else */
        if (queryParamValue) {
          const selectedFilter = filterSchemas.filter((schema) => {
            return schema.buckets.find((bucket) => {
              if (bucket.label === bucketLabel) {
                return bucket.options?.find(({ to, from, value, code }) => {
                  return code === optionCode && getOptionValue(value, to, from) === queryParamValue
                })
              }
              return false
            })
          })?.[0]

          /* v8 ignore else */
          if (selectedFilter) {
            set(
              needsBasedFilterMap,
              `needsBasedSelectedMapFromQueryParams.${selectedFilter.segmentLabel}.${bucketLabel}`,
              true
            )
          }

          set(
            needsBasedFilterMap,
            `needsBasedFilterDataFromQueryParams.${matchedParam.split('=')[0]}`,
            getValue(queryParamValue)
          )
        }

        return needsBasedFilterMap
      },
      { needsBasedFilterDataFromQueryParams: {}, needsBasedSelectedMapFromQueryParams: {} }
    ) || {}

  const { options: matchedOptions, labels: matchedLabels } = matchedNeedsBasedOptionsAndLabels({
    filterSchemas,
    filterData: merge(
      structuredClone(basicFilterDataFromQueryParams),
      needsBasedFilterDataFromQueryParams
    ),
  })

  return {
    basicFilterDataFromQueryParams,
    needsBasedQueryValuesFromQueryParams,
    needsBasedFilterDataFromQueryParams,
    needsBasedOptionsFromQueryParams: matchedOptions,
    needsBasedLabelsFromQueryParams: matchedLabels,
    needsBasedSelectedMapFromQueryParams,
  }
}

const getPath = (path: string) => {
  const [pathWithoutValue] = path.split('=')
  const cleanPath = pathWithoutValue.replace(/\+/gi, ' ')
  const [, objPath] = cleanPath.split('filters.')

  return decodeURIComponent(objPath)
}

const getValue = (value: string) => {
  const decodedValue = decodeURIComponent(value).split(',')
  const cleanValue = decodedValue.map((val) => val.replace(/\+/gi, ' '))

  return cleanValue
}
